"use client";

import UserAvatar from "@/components/Avatar/UserAvatar";
import { useEffect, useRef, useState } from "react";
import Button from "../../../ui/Button/Button";
import ProgressButton from "../../../ui/Button/ProgressButton";
import { useSession } from "next-auth/react";
import { useRouter } from "@/i18n/navigation";
import { useSearchParams } from "next/navigation";
import { toast } from "react-toastify";
import { loadStripe } from "@stripe/stripe-js";
import { useForm } from "react-hook-form";
import moment from "moment-timezone";
// import "moment/locale/fr";
import { capitalize } from "@/helpers/capitalize";
import Hour from "@/components/ui/Calendar/Hour/Hour";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutFormMentor from "@/components/ui/CheckoutForm/CheckoutFormMentor";
import Calendar from "@/components/ui/Calendar/Calendar";
import GuestBookingForm from "./GuestBookingForm";
import MeetingPreferenceSelector from "./MeetingPreferenceSelector";
import { isValidPhoneNumber } from "react-phone-number-input";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { Link } from "@/i18n/navigation";
import { SpinnerCircular } from "spinners-react";
import { motion } from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import { isMentorBookable } from "@/helpers/mentorBookable";
import useCountryOptions from "@/hooks/useCountryOptions";
import {
    getPostalCodeError,
    getPostalCodeField,
    isPostalCodeRequired,
    normalizePostalCode,
} from "@/lib/billingAddress";

// Adresse complète pour facturer un appel : rue, ville, pays, et code postal
// là où la poste du pays l'exige (lib/billingAddress.js). L'exiger partout
// bloquait les pays qui n'en ont pas (Côte d'Ivoire, Cameroun, Bénin...).
const isBillingAddressComplete = (street, postalCode, city, country) =>
    Boolean(
        street &&
            city &&
            country &&
            (postalCode || !isPostalCodeRequired(country)),
    );

export default function BookNewCall({
    mentor,
    prospectionMode = false,
    prospectionToken = null,
}) {
    const { data: session } = useSession();
    const router = useRouter();
    const searchParams = useSearchParams();
    const code = searchParams.get("code");
    const accelerator = searchParams.get("accelerator");
    const t = useTranslations();
    const locale = useLocale();

    const {
        register: register2,
        handleSubmit: handleSubmit2,
        formState: { errors: errors2 },
    } = useForm();
    const stripePromise = loadStripe(process.env.STRIPE_PUBLIC_KEY);

    // Sur le lien de prospection, on va droit au but : directement le calendrier (pas d'écran d'intro).
    const [bookStep, setBookStep] = useState(prospectionMode ? 3 : 1);
    const [editingAddress, setEditingAddress] = useState(false);
    const [addressStreet, setAddressStreet] = useState("");
    const [addressOption, setAddressOption] = useState("");
    const [addressPostal, setAddressPostal] = useState("");
    const [addressCity, setAddressCity] = useState("");
    const [addressCountry, setAddressCountry] = useState("");
    // Formulaire d'adresse : pays triés dans la langue du visiteur, champ code
    // postal réglé sur le pays choisi (même règle que /pricing et Mon compte).
    // pickedCountry reste null tant qu'on n'a pas touché au pays : on suit
    // alors l'adresse enregistrée, la France à défaut (comme à l'envoi).
    const countryOptions = useCountryOptions();
    const [pickedCountry, setPickedCountry] = useState(null);
    const billingCountry = pickedCountry ?? (addressCountry || "FR");
    const billingPostalCodeField = getPostalCodeField(billingCountry);
    const [codeUsed, setCodeUsed] = useState(null);
    const [hasFreeCode, setHasFreeCode] = useState(false);
    const [discoveryEligible, setDiscoveryEligible] = useState(false);
    const [bookIsPaid, setBookIsPaid] = useState(false);
    const [numberOfMinutesPerCall, setNumberOfMinutesPerCall] = useState(
        mentor && mentor.minutesPerCall ? mentor.minutesPerCall : null,
    );
    const [isLoadingAcceleratorAccess, setIsLoadingAcceleratorAccess] =
        useState(false);
    const [user, setUser] = useState(mentor);
    // L'appel gratuit ne s'active QUE via le lien de prospection dédié (prospectionMode),
    // jamais sur la page publique. Sur ce lien, l'appel est gratuit pour tout le monde.
    const isProspection = !!prospectionMode;
    // Le formulaire invité ne concerne que les visiteurs DÉCONNECTÉS. Connecté + lien de
    // prospection → l'appel gratuit est lié au compte (on réutilise le flux free-call).
    const isGuestBooking = !session && isProspection;
    // Appel découverte offert : flux membre (hors prospection) où le mentor l'a
    // activé et le membre est éligible. On saute le paiement.
    const isDiscoveryBooking = discoveryEligible && !hasFreeCode;
    // Vitrine : visiteur NON connecté sur la page publique. Si le mentor a
    // RÉELLEMENT activé l'appel découverte (mentorAllowsFreeDiscoveryCall), on
    // met l'offre en avant pour inciter à s'inscrire plutôt que d'afficher le
    // prix. L'éligibilité réelle est revérifiée une fois connecté.
    const showDiscoveryTeaser =
        !session &&
        !isProspection &&
        !hasFreeCode &&
        !codeUsed &&
        !!(user && user.mentorAllowsFreeDiscoveryCall);
    const [slots, setSlots] = useState({});
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [selectedDateAvailableHours, setSelectedDateAvailableHours] =
        useState([]);
    const [selectedTimezone, setSelectedTimezone] = useState(
        session && session.user ? session.user.timezone : "Europe/Paris",
    );
    // Format d'affichage des créneaux (bascule 12h/24h, défaut selon la langue).
    const [timeFormat, setTimeFormat] = useState(
        locale === "en" ? "12h" : "24h",
    );
    // Écran de confirmation prospection (utilisateur connecté) : objet + préférence d'échange
    const [freeCallSubject, setFreeCallSubject] = useState("");
    const [freeCallPreference, setFreeCallPreference] = useState("");
    const [freeCallPhone, setFreeCallPhone] = useState("");
    const [clientSecret, setClientSecret] = useState(null);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [student, setStudent] = useState(null);
    const [displayDarkStripe, setDisplayDarkStripe] = useState(false);
    const [creating, setCreating] = useState(false);
    const [selectedHour, setSelectedHour] = useState(null);

    const bookRef = useRef(null);
    const slotsRef = useRef(null);

    const onBookCallHandler = () => {
        if (bookStep == 1) {
            // Lien de prospection : appel gratuit pour tout le monde → direct au calendrier.
            if (isProspection) {
                setBookStep(3);
                return;
            }
            if (!session) {
                // On renvoie vers la connexion en mémorisant la page du mentor
                // pour y revenir une fois connecté (inscription : on s'en fiche).
                // Chemin INTERNE (next-intl le localise) construit depuis le
                // pseudo : usePathname() renverrait le template "/mentoring/[pseudo]".
                const mentorPath = `/mentoring/${
                    user.mentorPseudo
                        ? user.mentorPseudo
                        : `believer-${user.id}`
                }`;
                // Le code d'appel offert (?code=) suit la personne : sans
                // lui, elle revenait sur la page de l'appel payant.
                const redirectPath = code
                    ? `${mentorPath}?code=${encodeURIComponent(code)}`
                    : mentorPath;
                router.push(
                    `/login?redirect=${encodeURIComponent(redirectPath)}`,
                );
                return;
            }

            if (!hasFreeCode && !isDiscoveryBooking) {
                setAddressStreet(session.user.addressStreet);
                setAddressOption(session.user.addressOption);
                setAddressPostal(session.user.addressPostal);
                setAddressCity(session.user.addressCity);
                setAddressCountry(session.user.addressCountry);
                if (
                    !isBillingAddressComplete(
                        session.user.addressStreet,
                        session.user.addressPostal,
                        session.user.addressCity,
                        session.user.addressCountry,
                    )
                ) {
                    setEditingAddress(true);
                }
                setBookStep(2);
            } else {
                setBookStep(3);
            }
        } else if (bookStep == 2) {
            setBookStep(3);
        } else if (bookStep == 4) {
            if (!selectedSlot) {
                toast.error(t("BookNewCall_pleaseSelectDateAndHour"));
                return;
            }
            if (isGuestBooking) {
                setBookStep("guestForm");
                return;
            }
            if (isProspection) {
                // Connecté + lien de prospection → confirmation, puis réservation liée au compte.
                setBookStep("bookFreeCall");
                return;
            }
            if (isDiscoveryBooking) {
                // Membre éligible à l'appel découverte → confirmation, puis réservation gratuite.
                setBookStep("bookDiscovery");
                return;
            }
            if (!hasFreeCode) {
                // Appel payant : on demande d'abord l'objet + la préférence
                // d'échange (étape dédiée), puis on crée l'intent au moment de
                // confirmer ces informations.
                setBookStep("bookDetails");
            } else {
                setBookStep("bookFreeCall");
            }
        }
    };
    // Confirme l'objet + la préférence (étape "bookDetails") puis crée l'intent
    // de paiement et bascule sur l'écran de paiement.
    const onConfirmCallDetails = () => {
        if (
            !freeCallSubject.trim() ||
            !freeCallPreference ||
            (freeCallPreference === "phone" &&
                !isValidPhoneNumber(freeCallPhone || ""))
        ) {
            return;
        }
        fetch("/api/sale/mentor", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                price:
                    codeUsed && codeUsed.price
                        ? codeUsed.price
                        : user.pricePerCall,
                mentorPseudo: user.mentorPseudo,
                date: selectedSlot.start.utc().format("YYYY-MM-DD"),
                hour: selectedSlot.start.utc().format("HH:mm"),
                mentorTimezone: user.timezone,
                menteeTimezone: selectedTimezone,
                codeUsed,
                mentorStripeAccount: user.stripeAccount,
                mentorCommissionFees: user.mentorCommissionFees,
                subject: freeCallSubject || null,
                meetingPreference: freeCallPreference || "",
                guestPhone:
                    freeCallPreference === "phone" &&
                    isValidPhoneNumber(freeCallPhone || "")
                        ? freeCallPhone
                        : "",
            }),
        })
            .then((res) => res.json())
            .then((data) => setClientSecret(data.clientSecret));
        setBookStep(5);
    };
    const onBackHandler = () => {
        if (bookStep == 2) {
            setBookStep(1);
        } else if (bookStep == 3) {
            // L'appel découverte (comme un code gratuit) saute l'étape facturation :
            // le retour doit revenir à l'écran d'intro, pas à la facturation.
            if (!hasFreeCode && !isDiscoveryBooking) {
                setBookStep(2);
            } else {
                setBookStep(1);
            }
        } else if (bookStep == 4) {
            setSelectedDate(null);
            setSelectedHour(null);
            setBookStep(3);
        } else if (bookStep == "bookDetails") {
            setBookStep(4);
        } else if (bookStep == 5) {
            // L'écran de paiement n'existe que pour le flux payant : on revient
            // sur l'étape des informations (objet + préférence).
            setBookStep("bookDetails");
        } else if (bookStep == "bookFreeCall") {
            setSelectedHour(null);
            setBookStep(3);
        } else if (bookStep == "bookDiscovery") {
            setSelectedHour(null);
            setBookStep(4);
        } else if (bookStep == "guestForm") {
            setBookStep(4);
        }
    };
    const updateUserFacturation = async (data) => {
        if (!updating) {
            setUpdating(true);

            if (data.addresscountry == null) {
                data.addresscountry = "FR";
            }
            // Code postal remis en forme, et vidé si le pays choisi n'en
            // utilise pas (le champ masqué garde sa dernière saisie)
            data.addressPostal = normalizePostalCode(
                data.addresscountry,
                data.addressPostal,
            );

            const response = await fetch("/api/user/update-billing", {
                method: "post",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    addresscity: data.addressCity,
                    addresscountry: data.addresscountry,
                    addressoption: data.addressOption,
                    addresspostal: data.addressPostal,
                    addressstreet: data.addressStreet,
                }),
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                setUpdating(false);
                toast.error(fetchedData.message);
            } else {
                const event = new Event("visibilitychange");
                document.dispatchEvent(event);
                // On réaffiche ce qui vient d'être enregistré : la session n'est
                // rafraîchie qu'après coup (l'event ci-dessus), donc la relire ici
                // réafficherait l'ancienne adresse.
                setAddressStreet(data.addressStreet);
                setAddressOption(data.addressOption);
                setAddressPostal(data.addressPostal);
                setAddressCity(data.addressCity);
                setAddressCountry(data.addresscountry);
                setPickedCountry(null);
                if (
                    !isBillingAddressComplete(
                        data.addressStreet,
                        data.addressPostal,
                        data.addressCity,
                        data.addresscountry,
                    )
                ) {
                    setEditingAddress(true);
                }
                toast.success(t("BookNewCall_informationsChanged_success"));
                setUpdating(false);
                setBookStep(1);
                setEditingAddress(false);
            }
        }
    };
    const onBookFreeCallHandler = async () => {
        if (!updating) {
            setUpdating(true);

            // Check if code is valid (only for accelerator, promo codes are already validated in useEffect)
            if (accelerator) {
                const responseCode = await fetch(
                    "/api/call/get/accelerator-access",
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            acceleratorId: accelerator,
                            mentorPseudo: mentor?.pseudo,
                        }),
                    },
                );

                const fetchedCode = await responseCode.json();

                if (!responseCode.ok) {
                    toast.error(t("BookNewCall_cantBookWithThisMentor"));
                    setBookStep(1);
                    setHasFreeCode(false);
                    setUpdating(false);
                    return;
                }

                // Check if code is still valid
                if (!fetchedCode.isValid) {
                    toast.error(t("BookNewCall_cantBookWithThisMentor"));
                    setBookStep(1);
                    setHasFreeCode(false);
                    setUpdating(false);
                    return;
                }
            }

            const response = await fetch("/api/call/post/free-call", {
                method: "post",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    code: codeUsed,
                    date: selectedSlot.start.utc().format("YYYY-MM-DD"),
                    hour: selectedSlot.start.utc().format("HH:mm"),
                    mentorTimezone: user.timezone,
                    menteeTimezone: selectedTimezone,
                    mentorPseudo: user.mentorPseudo,
                    codeUsed,
                    mentor: user,
                    selectedSlot: selectedSlot,
                    student: student?.id ?? student,
                    accelerator: accelerator,
                    prospectionToken,
                    subject: isProspection ? freeCallSubject : null,
                    meetingPreference: isProspection ? freeCallPreference : "",
                    guestPhone:
                        isProspection && freeCallPreference === "phone"
                            ? freeCallPhone
                            : "",
                    locale,
                    numberOfMinutesPerCall,
                }),
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                setUpdating(false);
                toast.error(fetchedData.message);
            } else {
                const event = new Event("visibilitychange");
                document.dispatchEvent(event);
                setBookIsPaid(true);
                setUpdating(false);
            }
        }
    };
    const onBookDiscoveryHandler = async () => {
        if (updating) return;
        setUpdating(true);
        const response = await fetch("/api/call/post/discovery-call", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                mentorPseudo: user.mentorPseudo,
                date: selectedSlot.start.utc().format("YYYY-MM-DD"),
                hour: selectedSlot.start.utc().format("HH:mm"),
                mentorTimezone: user.timezone,
                menteeTimezone: selectedTimezone,
                mentor: user,
                selectedSlot: selectedSlot,
                subject: freeCallSubject || null,
                meetingPreference: freeCallPreference || "",
                guestPhone:
                    freeCallPreference === "phone" ? freeCallPhone : "",
                locale,
                numberOfMinutesPerCall,
            }),
        });
        const fetchedData = await response.json();
        if (!response.ok) {
            setUpdating(false);
            toast.error(fetchedData.message);
        } else {
            const event = new Event("visibilitychange");
            document.dispatchEvent(event);
            setBookIsPaid(true);
            setUpdating(false);
        }
    };
    const onSelectDateFromCalendar = (date) => {
        setSelectedSlot(null);
        setSelectedDate(date);
        setBookStep(4);
    };
    const onUpdateTimezone = async (timezone) => {
        if (timezone !== selectedTimezone) {
            // Le créneau choisi n'est plus valide dans le nouveau fuseau.
            setSelectedSlot(null);
            // On ré-ancre la date sur la même date calendaire dans le nouveau
            // fuseau : l'entête et les créneaux (dérivés) se rafraîchissent tout seuls.
            if (selectedDate) {
                setSelectedDate(
                    moment.tz(
                        selectedDate.format("YYYY-MM-DD"),
                        "YYYY-MM-DD",
                        timezone,
                    ),
                );
            }
        }
        setSelectedTimezone(timezone);
        if (session && session.user && timezone != session.user.timezone) {
            await fetch("/api/user/put/timezone", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    timezone,
                }),
            });
            const event = new Event("visibilitychange");
            document.dispatchEvent(event);
        }
    };

    useEffect(() => {
        if (accelerator && mentor && mentor.pseudo) {
            setIsLoadingAcceleratorAccess(true);
            fetch("/api/call/get/accelerator-access", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    acceleratorId: accelerator,
                    mentorPseudo: mentor.pseudo,
                }),
            })
                .then((res) => res.json())
                .then((data) => {
                    if (data.isValid) {
                        setHasFreeCode(true);
                        setCodeUsed(`accelerator-${accelerator}`);
                        setStudent(data.student);
                        setNumberOfMinutesPerCall(data.numberOfMinutesPerCall);
                    } else {
                        toast.error(t("BookNewCall_cantBookWithThisMentor"));
                        setHasFreeCode(false);
                    }
                })
                .finally(() => {
                    setIsLoadingAcceleratorAccess(false);
                });
        }
    }, [mentor.pseudo, accelerator]);

    useEffect(() => {
        // Appel découverte : flux membre uniquement (hors prospection / accélérateur / code).
        if (
            session &&
            session.user &&
            mentor &&
            mentor.mentorPseudo &&
            mentor.mentorAllowsFreeDiscoveryCall &&
            !isProspection &&
            !accelerator &&
            !code
        ) {
            fetch("/api/call/get/discovery-eligibility", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ mentorPseudo: mentor.mentorPseudo }),
            })
                .then((res) => res.json())
                .then((data) => {
                    if (data && data.available) {
                        setDiscoveryEligible(true);
                        if (data.duration) {
                            setNumberOfMinutesPerCall(data.duration);
                        }
                    }
                })
                .catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mentor.mentorPseudo, session]);

    useEffect(() => {
        if (code && mentor && (mentor.mentorPseudo || mentor.pseudo)) {
            fetch("/api/call/get/code-access", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    code: code,
                    // Le back cherche le mentor par son pseudo de MENTOR, qui
                    // peut différer du pseudo du compte (comme le paiement,
                    // CheckoutFormMentor) : avec l'autre, un code valide était
                    // déclaré invalide.
                    mentorPseudo: mentor.mentorPseudo || mentor.pseudo,
                }),
            })
                .then((res) => res.json())
                .then((data) => {
                    if (data.isValid) {
                        // toast.success(t("BookNewCall_code_added"));
                        if (data.code.type == "free") {
                            setHasFreeCode(true);
                        }
                        setCodeUsed(data.code);
                    } else {
                        toast.error(t("BookNewCall_code_invalid"));
                        setHasFreeCode(false);
                    }
                });
        }
    }, [mentor.pseudo, mentor.mentorPseudo, code, session?.user?.userId]);

    useEffect(() => {
        // Ne définir numberOfMinutesPerCall depuis user.minutesPerCall que si ce n'est pas déjà défini
        // (par exemple depuis l'accès accélérateur)
        if (user && user.minutesPerCall && !numberOfMinutesPerCall) {
            setNumberOfMinutesPerCall(user.minutesPerCall);
        }
    }, [user]);

    useEffect(() => {
        document.body.style.overflow = isSendingMessage ? "hidden" : "auto";
    }, [isSendingMessage]);

    useEffect(() => {
        moment.locale(locale);
    }, [locale]);

    function restartBooking() {
        setBookStep(3);
        setClientSecret(null);
        setSelectedSlot(null);
    }

    function restartFullBooking() {
        setBookStep(1);
        setCodeUsed(null);
        setClientSecret(null);
        setSelectedSlot(null);
    }

    // Les créneaux du jour sélectionné sont DÉRIVÉS de `slots` + `selectedDate` :
    // un changement de fuseau recalcule `slots`, ce qui rafraîchit immédiatement
    // l'affichage sans avoir à recliquer sur le jour.
    useEffect(() => {
        if (!selectedDate) {
            setSelectedDateAvailableHours([]);
            return;
        }
        const dayKey = selectedDate.format("YYYY-MM-DD");
        if (slots[dayKey] && slots[dayKey].slots.length > 0) {
            setSelectedDateAvailableHours(slots[dayKey].slots);
        } else {
            setSelectedDateAvailableHours([]);
        }
    }, [selectedDate, slots]);

    useEffect(() => {
        if (selectedSlot) {
            bookRef.current.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "start",
            });
        }
    }, [selectedSlot]);

    // Choix d'une date : sur mobile (créneaux empilés sous le calendrier), on amène
    // les créneaux à l'écran en douceur. Sur desktop (3 colonnes), ils sont déjà
    // visibles à droite, donc on ne scrolle pas.
    useEffect(() => {
        if (
            isProspection &&
            selectedDate &&
            selectedDateAvailableHours.length > 0 &&
            slotsRef.current &&
            typeof window !== "undefined" &&
            window.innerWidth < 1024
        ) {
            slotsRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDateAvailableHours]);

    // Passage à l'écran final (coordonnées / confirmation) ou au succès : on remonte
    // tout en haut pour repartir proprement du début de l'écran.
    useEffect(() => {
        if (
            isProspection &&
            (bookStep === "guestForm" ||
                bookStep === "bookFreeCall" ||
                bookIsPaid) &&
            typeof window !== "undefined"
        ) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookStep, bookIsPaid]);

    useEffect(() => {
        if (session && session.user && session.user.darkMode) {
            setDisplayDarkStripe("night");
        } else {
            setDisplayDarkStripe("flat");
        }
    }, [session]);

    // Étape "deux-panneaux" (calendrier + créneaux) du flux prospection.
    // Colonne hôte (rail de gauche) persistante entre la sélection du créneau
    // et le formulaire. En mode résumé, elle affiche le créneau choisi pour
    // garder une continuité visuelle (façon Cal.com).
    const hostColumn = (showSummary) => (
        <div className="w-full lg:w-[210px] flex-shrink-0 lg:pr-7">
            {user.photo && (
                <div className="relative w-12 h-12 mb-3 rounded-full overflow-hidden ring-1 ring-black/5 dark:ring-white/10">
                    <UserAvatar
                        preferPhoto
                        user={user}
                        alt={`${user.firstName} ${user.lastName}`}
                        fill
                        sizes="48px"
                        className="object-cover"
                    />
                </div>
            )}
            <div className="text-[13px] text-gray-500 dark:text-grayDark-400">
                {user.firstName} {user.lastName}
            </div>
            <h1 className="mt-1 text-xl font-bold tracking-tight text-gray-900 dark:text-white leading-tight">
                {t("ProspectionPage_default_title")}
            </h1>
            <p
                className={`${
                    showSummary ? "hidden lg:block " : ""
                }mt-3 mb-0 text-[13px] text-gray-500 dark:text-grayDark-400 leading-relaxed`}
            >
                {t("ProspectionPage_default_description")}
            </p>
            {showSummary && selectedSlot && (
                <div className="mt-4 flex items-start gap-2 text-[13px]">
                    <SolarIcon
                        name="calendar-linear"
                        className="text-sm text-primary flex-shrink-0 mt-0.5"
                    />
                    <div className="leading-snug">
                        <div className="font-semibold text-gray-900 dark:text-white capitalize">
                            {capitalize(
                                selectedSlot.start
                                    .tz(selectedTimezone)
                                    .locale(locale)
                                    .format("dddd D MMMM"),
                            )}
                        </div>
                        <div className="text-gray-500 dark:text-grayDark-400">
                            {selectedSlot.start
                                .tz(selectedTimezone)
                                .format(
                                    timeFormat === "12h" ? "h:mm A" : "HH:mm",
                                )}
                        </div>
                    </div>
                </div>
            )}
            {(numberOfMinutesPerCall || user.minutesPerCall) && (
                <div
                    className={`${
                        showSummary ? "mt-3" : "mt-4"
                    } flex items-center gap-2 text-[13px] text-gray-500 dark:text-grayDark-400`}
                >
                    <SolarIcon
                        name="clock-circle-linear"
                        className="text-sm text-gray-400 flex-shrink-0"
                    />
                    {numberOfMinutesPerCall || user.minutesPerCall} min
                </div>
            )}
            {showSummary ? (
                <div className="mt-3 flex items-center gap-2 text-[13px] text-gray-500 dark:text-grayDark-400">
                    <SolarIcon
                        name="global-linear"
                        className="text-sm text-gray-400 flex-shrink-0"
                    />
                    <span className="truncate">{selectedTimezone}</span>
                </div>
            ) : (
                <div className="mt-3 flex items-center gap-2 text-[13px] text-gray-500 dark:text-grayDark-400">
                    <SolarIcon
                        name="global-linear"
                        className="text-sm text-gray-400 flex-shrink-0"
                    />
                    <div className="relative flex-1 min-w-0">
                        <select
                            className="appearance-none w-full bg-transparent text-[13px] text-gray-500 dark:text-grayDark-400 cursor-pointer focus:outline-hidden truncate p-0 pr-5"
                            value={selectedTimezone}
                            onChange={(e) => onUpdateTimezone(e.target.value)}
                        >
                            {moment.tz.names().map((tz) => (
                                <option key={tz} value={tz}>
                                    {tz} - {moment().tz(tz).format("HH:mm")}
                                </option>
                            ))}
                        </select>
                        <SolarIcon
                            name="alt-arrow-down-linear"
                            className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-xs text-gray-400"
                        />
                    </div>
                </div>
            )}
        </div>
    );

    // Ligne de "feature" façon Stripe : tuile d'icône + titre + détail optionnel.
    // Sans détail : on centre verticalement la tuile avec le titre (une ligne).
    // Avec détail : alignement haut, la tuile se cale sur la première ligne.
    const renderFeature = (icon, title, detail) => (
        <div className={`flex gap-3 ${detail ? "items-start" : "items-center"}`}>
            <span className="flex-shrink-0 w-9 h-9 rounded-xl bg-gray-100 dark:bg-white/5 flex items-center justify-center text-gray-500 dark:text-grayDark-400">
                <SolarIcon name={icon} className="text-base" />
            </span>
            <div className="min-w-0 text-sm">
                <div className="font-semibold text-gray-900 dark:text-white leading-snug">
                    {title}
                </div>
                {detail && (
                    <div className="text-[13px] text-gray-500 dark:text-grayDark-400 mt-0.5 leading-snug">
                        {detail}
                    </div>
                )}
            </div>
        </div>
    );

    // Délai d'annulation ("Jusqu'à X avant votre appel").
    const cancelTimingText = () => {
        const h = user.mentorMinimumHoursToCancelCall;
        if (!h || h == 0) return null;
        if (h % 24 === 0) {
            const d = h / 24;
            return d === 1
                ? t("BookNewCall_book_point_cancellationAvailable_day")
                : t("BookNewCall_book_point_cancellationAvailable_days", {
                      days: d,
                  });
        }
        return h === 1
            ? t("BookNewCall_book_point_cancellationAvailable_hour")
            : t("BookNewCall_book_point_cancellationAvailable_hours", {
                  hours: h,
              });
    };

    // Détail de modification (nombre de modifications + délai). L'appel découverte
    // hérite des mêmes fenêtres que les appels payants du mentor.
    const modificationDetail =
        user.mentorMinimumHoursToChangeCall &&
        user.mentorMinimumHoursToChangeCall != 0 ? (
            <>
                {user.mentorMinimumHoursToChangeCall % 24 == 0 ? (
                    <>
                        {user.mentorMinimumHoursToChangeCall / 24 == 1
                            ? t(
                                  "BookNewCall_book_point_modificationAvailable_day",
                                  {
                                      numberOfChanges:
                                          user.mentorMaximumChangeCall,
                                  },
                              )
                            : t(
                                  "BookNewCall_book_point_modificationAvailable_days",
                                  {
                                      numberOfChanges:
                                          user.mentorMaximumChangeCall,
                                      days:
                                          user.mentorMinimumHoursToChangeCall /
                                          24,
                                  },
                              )}
                    </>
                ) : (
                    <>
                        {user.mentorMinimumHoursToChangeCall}{" "}
                        {user.mentorMinimumHoursToChangeCall == 1
                            ? t(
                                  "BookNewCall_book_point_modificationAvailable_hour",
                                  {
                                      numberOfChanges:
                                          user.mentorMaximumChangeCall,
                                  },
                              )
                            : t(
                                  "BookNewCall_book_point_modificationAvailable_hours",
                                  {
                                      numberOfChanges:
                                          user.mentorMaximumChangeCall,
                                      hours: user.mentorMinimumHoursToChangeCall,
                                  },
                              )}
                    </>
                )}
                {user.mentorMaximumHoursWithNewCall ? (
                    <>
                        {" "}
                        {user.mentorMaximumHoursWithNewCall > 1
                            ? `(${t(
                                  "BookNewCall_book_modificationAvailableMaximumDaysAfterDescription",
                                  {
                                      days: user.mentorMaximumHoursWithNewCall,
                                  },
                              ).toLowerCase()})`
                            : `(${t(
                                  "BookNewCall_book_modificationAvailableMaximumOneDayAfterDescription",
                              ).toLowerCase()})`}
                    </>
                ) : (
                    "."
                )}
            </>
        ) : null;

    const isSelectStep = isProspection && (bookStep == 3 || bookStep == 4);
    const isFormStep =
        isProspection &&
        (bookStep === "guestForm" || bookStep === "bookFreeCall");
    const isTwoPaneStep = isSelectStep || isFormStep;
    const showSlotsPanel =
        selectedDate && selectedDateAvailableHours.length > 0;

    return (
        <div
            className={`bg-white dark:bg-box border dark:border-borderDark ${
                isProspection
                    ? `rounded-2xl p-6 sm:p-8 border-gray-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.06)] mx-auto ${
                          isTwoPaneStep
                              ? `overflow-hidden transition-[width] duration-300 ease-in-out w-full ${
                                    isSelectStep && !showSlotsPanel
                                        ? "lg:w-[624px]"
                                        : "lg:w-[844px]"
                                }`
                              : "w-full max-w-md"
                      }`
                    : "rounded-2xl p-6 border-gray-200 lg:max-h-176 overflow-y-auto"
            }`}
        >
            {bookIsPaid ? (
                <>
                    <h2 className="text-xl font-bold dark:text-white">
                        {t("BookNewCall_reservation_success")}
                    </h2>
                    <div className="mt-3 dark:text-lightBlue">
                        {t(
                            isGuestBooking
                                ? "BookNewCall_reservation_success_message_guest"
                                : "BookNewCall_reservation_success_message",
                            {
                                fullName: `${user.firstName} ${user.lastName}`,
                            },
                        )}
                    </div>
                </>
            ) : (
                <>
                    {bookStep == 1 && (
                        <>
                            {isLoadingAcceleratorAccess ? (
                                <>
                                    {/* Skeleton loading state */}
                                    <div className="animate-pulse">
                                        <div className="inline-flex flex-col gap-2">
                                            <div className="h-6 w-32 bg-gray-200 dark:bg-grayDark-700 rounded"></div>
                                            <div className="h-12 w-48 bg-gray-200 dark:bg-grayDark-700 rounded"></div>
                                        </div>
                                        <div className="mt-3 space-y-2">
                                            <div className="h-4 w-full bg-gray-200 dark:bg-grayDark-700 rounded"></div>
                                            <div className="h-4 w-3/4 bg-gray-200 dark:bg-grayDark-700 rounded"></div>
                                        </div>
                                        <div className="mt-4 space-y-3">
                                            <div className="flex gap-2 items-start">
                                                <div className="w-5 h-5 bg-gray-200 dark:bg-grayDark-700 rounded flex-shrink-0"></div>
                                                <div className="h-4 w-full bg-gray-200 dark:bg-grayDark-700 rounded"></div>
                                            </div>
                                            <div className="flex gap-2 items-start">
                                                <div className="w-5 h-5 bg-gray-200 dark:bg-grayDark-700 rounded flex-shrink-0"></div>
                                                <div className="h-4 w-full bg-gray-200 dark:bg-grayDark-700 rounded"></div>
                                            </div>
                                            <div className="flex gap-2 items-start">
                                                <div className="w-5 h-5 bg-gray-200 dark:bg-grayDark-700 rounded flex-shrink-0"></div>
                                                <div className="h-4 w-full bg-gray-200 dark:bg-grayDark-700 rounded"></div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : isProspection ? (
                                <div className="inline-flex flex-col gap-1">
                                    <div className="text-4xl font-bold text-confidence">
                                        {t("GuestBooking_free")}
                                    </div>
                                </div>
                            ) : hasFreeCode ? (
                                <>
                                    <div className="inline-flex flex-col gap-1">
                                        <div className="text-lg text-gray-500 dark:text-grayDark-400">
                                            <s>
                                                {user.pricePerCall} €{" "}
                                                {t("BookNewCall_priceLabel")}
                                            </s>
                                        </div>
                                        <div className="text-4xl font-bold text-green-600 dark:text-green-400">
                                            {t("BookNewCall_free")}
                                        </div>
                                    </div>
                                </>
                            ) : isDiscoveryBooking || showDiscoveryTeaser ? (
                                <div className="flex flex-col gap-2.5">
                                    <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-primary/10 text-primary px-2.5 py-1 text-xs font-semibold">
                                        <SolarIcon
                                            name="gift-linear"
                                            className="text-sm"
                                        />
                                        {t("BookNewCall_discovery_offer_badge")}
                                    </span>
                                    <div className="flex items-baseline gap-2.5">
                                        <span className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
                                            {t("BookNewCall_free")}
                                        </span>
                                        <span className="text-lg text-gray-400 dark:text-grayDark-500 line-through">
                                            {user.pricePerCall} €
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {codeUsed ? (
                                        <>
                                            {codeUsed.price <
                                            user.pricePerCall ? (
                                                <>
                                                    <div className="inline-flex flex-col gap-1">
                                                        <div className="text-lg text-gray-500 dark:text-grayDark-400">
                                                            <s>
                                                                {
                                                                    user.pricePerCall
                                                                }{" "}
                                                                €{" "}
                                                                {t(
                                                                    "BookNewCall_priceLabel",
                                                                )}
                                                            </s>
                                                        </div>
                                                        <div className="text-4xl font-bold text-gray-900 dark:text-white">
                                                            {codeUsed.price} €{" "}
                                                            <span className="text-xl text-gray-600 dark:text-grayDark-400">
                                                                {t(
                                                                    "BookNewCall_priceLabel",
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="text-4xl font-bold text-gray-900 dark:text-white">
                                                        {codeUsed.price} €{" "}
                                                        <span className="text-xl text-gray-600 dark:text-grayDark-400">
                                                            {t(
                                                                "BookNewCall_priceLabel",
                                                            )}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                            {codeUsed.maxUses -
                                                codeUsed.numberOfUses ==
                                                1 && (
                                                <div className="mt-3 flex gap-2 items-start p-3 rounded-lg bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        strokeWidth={1.5}
                                                        stroke="currentColor"
                                                        className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                                                        />
                                                    </svg>
                                                    <span className="text-sm text-red-900 dark:text-red-200">
                                                        {t(
                                                            "BookNewCall_onlyOneLeftForThisPrice",
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                            {codeUsed.maxUses -
                                                codeUsed.numberOfUses >
                                                1 &&
                                                codeUsed.maxUses -
                                                    codeUsed.numberOfUses <=
                                                    3 && (
                                                    <div className="mt-3 flex gap-2 items-start p-3 rounded-lg bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            strokeWidth={1.5}
                                                            stroke="currentColor"
                                                            className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                                                            />
                                                        </svg>
                                                        <span className="text-sm text-red-900 dark:text-red-200">
                                                            {t(
                                                                "BookNewCall_onlyFewCallsAvailableAtThisPrice",
                                                                {
                                                                    number:
                                                                        codeUsed.maxUses -
                                                                        codeUsed.numberOfUses,
                                                                },
                                                            )}
                                                        </span>
                                                    </div>
                                                )}
                                            {codeUsed.limitDate && (
                                                // If limit date is less than 24 hours using moment
                                                <>
                                                    {moment
                                                        .utc(codeUsed.limitDate)
                                                        .diff(
                                                            moment.utc(),
                                                            "hours",
                                                        ) <= 24 && (
                                                        <div className="mt-3 flex gap-2 items-start p-3 rounded-lg bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30">
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                                strokeWidth={
                                                                    1.5
                                                                }
                                                                stroke="currentColor"
                                                                className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
                                                                />
                                                            </svg>
                                                            <span className="text-sm text-red-900 dark:text-red-200">
                                                                {t(
                                                                    "BookNewCall_youHaveLessThan24HoursToEnjoyThisPrice",
                                                                    {
                                                                        time:
                                                                            moment
                                                                                .utc(
                                                                                    codeUsed.limitDate,
                                                                                )
                                                                                .diff(
                                                                                    moment.utc(),
                                                                                    "hours",
                                                                                ) <
                                                                            1
                                                                                ? moment
                                                                                      .utc(
                                                                                          codeUsed.limitDate,
                                                                                      )
                                                                                      .diff(
                                                                                          moment.utc(),
                                                                                          "minutes",
                                                                                      ) +
                                                                                  ` ${t(
                                                                                      "BookNewCall_minutes",
                                                                                  )}`
                                                                                : moment
                                                                                      .utc(
                                                                                          codeUsed.limitDate,
                                                                                      )
                                                                                      .diff(
                                                                                          moment.utc(),
                                                                                          "hours",
                                                                                      ) +
                                                                                  ` ${t(
                                                                                      "BookNewCall_hours",
                                                                                  )}`,
                                                                    },
                                                                )}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {/* If limit date is betwen 24 hours and 7 days using moment */}
                                                    {moment
                                                        .utc(codeUsed.limitDate)
                                                        .diff(
                                                            moment.utc(),
                                                            "hours",
                                                        ) > 24 &&
                                                        moment
                                                            .utc(
                                                                codeUsed.limitDate,
                                                            )
                                                            .diff(
                                                                moment.utc(),
                                                                "hours",
                                                            ) < 168 && (
                                                            <div className="text-red-400 mt-1 flex gap-1">
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                    strokeWidth={
                                                                        1.5
                                                                    }
                                                                    stroke="currentColor"
                                                                    className="w-6 h-6"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
                                                                    />
                                                                </svg>
                                                                <span>
                                                                    {moment
                                                                        .utc(
                                                                            codeUsed.limitDate,
                                                                        )
                                                                        .diff(
                                                                            moment.utc(),
                                                                            "days",
                                                                        ) > 1
                                                                        ? t(
                                                                              "BookNewCall_onlyFewDaysAvailableAtThisPrice",
                                                                              {
                                                                                  number: moment
                                                                                      .utc(
                                                                                          codeUsed.limitDate,
                                                                                      )
                                                                                      .diff(
                                                                                          moment.utc(),
                                                                                          "days",
                                                                                      ),
                                                                              },
                                                                          )
                                                                        : t(
                                                                              "BookNewCall_onlyOneDayAvailableAtThisPrice",
                                                                          )}
                                                                </span>
                                                            </div>
                                                        )}
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <div className="text-4xl font-bold">
                                                {user.pricePerCall} €{" "}
                                                <span className="text-xl">
                                                    {t(
                                                        "BookNewCall_priceLabel",
                                                    )}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                            {!isLoadingAcceleratorAccess && (
                                <>
                                    <div className="mt-3">
                                        {t("BookNewCall_bookDescription", {
                                            fullName: `${user.firstName} ${user.lastName}`,
                                            minutesPerCall:
                                                numberOfMinutesPerCall ||
                                                user.minutesPerCall,
                                        })}
                                    </div>
                                    <div className="mt-5 space-y-3.5">
                                        {renderFeature(
                                            "clock-circle-linear",
                                            t("BookNewCall_book_point_minutes", {
                                                minutesPerCall:
                                                    numberOfMinutesPerCall ||
                                                    user.minutesPerCall,
                                            }),
                                        )}
                                        {renderFeature(
                                            "tag-price-linear",
                                            t(
                                                "BookNewCall_book_point_hiddenFees",
                                            ),
                                        )}
                                        {renderFeature(
                                            "verified-check-linear",
                                            t(
                                                "BookNewCall_book_point_selection",
                                            ),
                                        )}
                                        {/* Annulation : l'appel découverte suit son propre réglage (et pas de remboursement, rien n'a été payé) */}
                                        {(isDiscoveryBooking
                                            ? user.mentorDiscoveryCallCancellable
                                            : user.mentorAuthorizeCancelCall) &&
                                            renderFeature(
                                                "close-circle-linear",
                                                isDiscoveryBooking ||
                                                    hasFreeCode ||
                                                    user.mentorRefundRate == 100
                                                    ? t(
                                                          "BookNewCall_book_freeCancellation",
                                                      )
                                                    : t(
                                                          "BookNewCall_book_point_cancellationAvailable",
                                                      ),
                                                isDiscoveryBooking ||
                                                    hasFreeCode ? (
                                                    cancelTimingText()
                                                ) : (
                                                    <>
                                                        {user.mentorRefundRate ==
                                                        100
                                                            ? t(
                                                                  "BookNewCall_book_fullRefund",
                                                              )
                                                            : t(
                                                                  "BookNewCall_book_partialRefund",
                                                                  {
                                                                      percent:
                                                                          user.mentorRefundRate,
                                                                  },
                                                              )}
                                                        {cancelTimingText() && (
                                                            <>
                                                                {" "}
                                                                {cancelTimingText().toLowerCase()}
                                                            </>
                                                        )}
                                                    </>
                                                ),
                                            )}
                                        {/* Droit de rétractation d'un appel payé (CGV, articles 11 et 14) : il passe avant les conditions du mentor */}
                                        {!isDiscoveryBooking &&
                                            !hasFreeCode &&
                                            !isProspection &&
                                            renderFeature(
                                                "shield-check-linear",
                                                t(
                                                    "BookNewCall_book_point_withdrawal",
                                                ),
                                                t(
                                                    "BookNewCall_book_point_withdrawal_detail",
                                                ),
                                            )}
                                        {/* Modification : l'appel découverte suit son propre réglage */}
                                        {(isDiscoveryBooking
                                            ? user.mentorDiscoveryCallModifiable
                                            : user.mentorAuthorizeChangeCall) &&
                                            renderFeature(
                                                "refresh-circle-linear",
                                                t(
                                                    "BookNewCall_book_modificationAvailable",
                                                ),
                                                modificationDetail,
                                            )}
                                    </div>
                                    <div className="mt-5">
                                        {/* <Link
        href={`/mentoring/${user.pseudo}/book`}
    >
        <a> */}
                                        {/* {false ? ( */}
                                        {/* « Bientôt » tant que le mentor n'est
                                            pas réellement réservable : la case
                                            « disponibilités ouvertes » seule
                                            menait vers un calendrier vide. */}
                                        {isMentorBookable(user) ? (
                                            <Button
                                                type="primary"
                                                className="w-full"
                                                onClick={onBookCallHandler}
                                            >
                                                {t(
                                                    "BookNewCall_button_bookACall",
                                                )}
                                            </Button>
                                        ) : (
                                            <Button
                                                isLoading={true}
                                                className="w-full"
                                            >
                                                {t("BookNewCall_button_soon")}
                                            </Button>
                                        )}
                                        {/* </a>
    </Link> */}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                    {bookStep == 2 && (
                        <>
                            <div className="flex items-center justify-between mb-4">
                                <button
                                    className="inline-flex items-center justify-center text-gray-400 cursor-pointer hover:text-gray-700 dark:text-grayDark-500 dark:hover:text-grayDark-200 transition-colors p-2 -ml-2"
                                    onClick={onBackHandler}
                                    aria-label="Retour"
                                    type="button"
                                >
                                    <SolarIcon
                                        name="alt-arrow-left-linear"
                                        className="text-2xl"
                                    />
                                </button>

                                <h2 className="text-xl font-bold dark:text-white flex-1">
                                    {t("BookNewCall_address_title")}
                                </h2>

                                <div className="w-10"></div>
                            </div>
                            {isBillingAddressComplete(
                                addressStreet,
                                addressPostal,
                                addressCity,
                                addressCountry,
                            ) &&
                                !editingAddress && (
                                    <>
                                        <div className="mt-4 rounded-xl border border-gray-100 dark:border-borderDark bg-gray-50 dark:bg-bgDark-900/40 p-4 flex gap-3">
                                            <span className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-lg bg-white dark:bg-bgDark-840 border border-gray-100 dark:border-borderDark flex items-center justify-center text-gray-400 shadow-sm">
                                                <SolarIcon name="home-smile-linear" className="text-base" />
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                {/* Adresse de FACTURATION : le nom affiché est celui du
                                                    membre connecté, pas celui du mentor (`user` = le
                                                    mentor consulté dans tout ce composant). */}
                                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                                    {session?.user?.firstName}{" "}
                                                    {session?.user?.lastName}
                                                </div>
                                                <div className="mt-1 text-sm text-gray-500 dark:text-grayDark-400 leading-relaxed">
                                                    {addressStreet}
                                                    {addressOption && (
                                                        <>, {addressOption}</>
                                                    )}
                                                    <br />
                                                    {addressPostal && <>{addressPostal} </>}
                                                    {capitalize(addressCity)}
                                                    <br />
                                                    {/* Le nom du pays plutôt que son code (« CI ») */}
                                                    {t.has(`address_selectedCountry_${addressCountry}`)
                                                        ? t(`address_selectedCountry_${addressCountry}`)
                                                        : addressCountry}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setEditingAddress(true)}
                                                title={t("BookNewCall_address_updateButton")}
                                                className="flex-shrink-0 self-start p-1 text-gray-400 hover:text-primary transition-colors"
                                            >
                                                <SolarIcon name="pen-2-linear" className="text-base" />
                                            </button>
                                        </div>
                                        <div className="mt-6">
                                            <Button
                                                type="primary"
                                                block
                                                onClick={onBookCallHandler}
                                            >
                                                {t("BookNewCall_button_next")}
                                            </Button>
                                        </div>
                                    </>
                                )}
                            {editingAddress && (
                                <div className="mt-4">
                                    <form
                                        onSubmit={handleSubmit2(
                                            updateUserFacturation,
                                        )}
                                    >
                                        {/* Le pays d'abord : il décide du champ code postal
                                            (masqué là où il n'existe pas, format vérifié aux
                                            États-Unis et au Canada) */}
                                        <div className="w-full">
                                            <label
                                                htmlFor="addressCountry"
                                                className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-grayDark-400 mb-1.5 block"
                                            >
                                                {t("address_country")}
                                            </label>
                                            <select
                                                id="addressCountry"
                                                className="input"
                                                autoComplete="country"
                                                {...register2(
                                                    "addresscountry",
                                                    {
                                                        required: {
                                                            value: true,
                                                            message: t(
                                                                "address_country_required",
                                                            ),
                                                        },
                                                        value: addressCountry || "FR",
                                                        onChange: (event) =>
                                                            setPickedCountry(
                                                                event.target.value,
                                                            ),
                                                    },
                                                )}
                                            >
                                                {countryOptions.map((country) => (
                                                    <option
                                                        key={country.code}
                                                        value={country.code}
                                                    >
                                                        {country.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="w-full mt-4">
                                            <label
                                                htmlFor="addressStreet"
                                                className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-grayDark-400 mb-1.5 block"
                                            >
                                                {t("address_street")}
                                            </label>
                                            <input
                                                type="text"
                                                name="addressStreet"
                                                className="input"
                                                id="addressStreet"
                                                placeholder={t(
                                                    "address_street",
                                                )}
                                                {...register2("addressStreet", {
                                                    required: {
                                                        value: true,
                                                        message: t(
                                                            "address_street_required",
                                                        ),
                                                    },
                                                    value: addressStreet,
                                                })}
                                            />
                                            {errors2 &&
                                                errors2.addressStreet && (
                                                    <div className="text-red-400 mt-1">
                                                        {
                                                            errors2
                                                                .addressStreet
                                                                .message
                                                        }
                                                    </div>
                                                )}
                                        </div>
                                        <div className="w-full mt-4">
                                            <label
                                                htmlFor="addressOption"
                                                className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-grayDark-400 mb-1.5 block"
                                            >
                                                {t("address_option")}
                                            </label>
                                            <input
                                                type="text"
                                                name="addressOption"
                                                className="input"
                                                id="addressOption"
                                                placeholder={t(
                                                    "address_option",
                                                )}
                                                {...register2("addressOption", {
                                                    value: addressOption,
                                                })}
                                            />
                                        </div>
                                        <div className="mt-4 flex gap-4">
                                            {billingPostalCodeField.visible && (
                                                <div className="w-full">
                                                    <label
                                                        htmlFor="addressPostal"
                                                        className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-grayDark-400 mb-1.5 block"
                                                    >
                                                        {t(
                                                            billingPostalCodeField.labelKey,
                                                        )}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="addressPostal"
                                                        name="addressPostal"
                                                        className="input"
                                                        placeholder={
                                                            billingPostalCodeField.example
                                                        }
                                                        autoComplete="postal-code"
                                                        inputMode={
                                                            billingPostalCodeField.inputMode
                                                        }
                                                        autoCapitalize={
                                                            billingPostalCodeField.autoCapitalize
                                                        }
                                                        spellCheck={false}
                                                        {...register2(
                                                            "addressPostal",
                                                            {
                                                                validate: (value) => {
                                                                    const errorKey =
                                                                        getPostalCodeError(
                                                                            billingCountry,
                                                                            normalizePostalCode(
                                                                                billingCountry,
                                                                                value,
                                                                            ),
                                                                        );
                                                                    return errorKey
                                                                        ? t(errorKey)
                                                                        : true;
                                                                },
                                                                value: addressPostal,
                                                            },
                                                        )}
                                                    />
                                                    {errors2 &&
                                                        errors2.addressPostal && (
                                                            <div className="text-red-400 mt-1">
                                                                {
                                                                    errors2
                                                                        .addressPostal
                                                                        .message
                                                                }
                                                            </div>
                                                        )}
                                                </div>
                                            )}
                                            <div className="w-full">
                                                <label
                                                    htmlFor="addressCity"
                                                    className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-grayDark-400 mb-1.5 block"
                                                >
                                                    {t("address_city")}
                                                </label>
                                                <input
                                                    type="text"
                                                    id="addressCity"
                                                    name="addressCity"
                                                    className="input"
                                                    placeholder={t(
                                                        "address_city",
                                                    )}
                                                    {...register2(
                                                        "addressCity",
                                                        {
                                                            required: {
                                                                value: true,
                                                                message: t(
                                                                    "address_city_required",
                                                                ),
                                                            },
                                                            value: addressCity,
                                                        },
                                                    )}
                                                />
                                                {errors2 &&
                                                    errors2.addressCity && (
                                                        <div className="text-red-400 mt-1">
                                                            {
                                                                errors2
                                                                    .addressCity
                                                                    .message
                                                            }
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                        <div className="mt-6">
                                            <Button
                                                isLoading={updating}
                                                progress
                                                type="primary"
                                                block
                                            >
                                                {t("BookNewCall_button_next")}
                                            </Button>
                                        </div>
                                    </form>
                                </div>
                            )}
                        </>
                    )}
                    {!isProspection && bookStep == 3 && (
                        <>
                            <div className="flex items-center justify-between mb-4">
                                {!isProspection && (
                                    <button
                                        className="inline-flex items-center justify-center text-gray-400 cursor-pointer hover:text-gray-700 dark:text-grayDark-500 dark:hover:text-grayDark-200 transition-colors p-2 -ml-2"
                                        onClick={onBackHandler}
                                        aria-label="Retour"
                                        type="button"
                                    >
                                        <SolarIcon
                                            name="alt-arrow-left-linear"
                                            className="text-2xl"
                                        />
                                    </button>
                                )}

                                <h2 className="text-xl font-bold dark:text-white flex-1">
                                    {t("BookNewCall_calendar_title")}
                                </h2>
                            </div>
                            <div className="mt-3">
                                {/* First day of moment */}
                                <Calendar
                                    selectedDate={onSelectDateFromCalendar}
                                    mentor={user}
                                    selectedTimezone={selectedTimezone}
                                    mentee={
                                        session
                                            ? session.user
                                            : { timezone: selectedTimezone }
                                    }
                                    changeTimezone={onUpdateTimezone}
                                    setSlots={setSlots}
                                    numberOfMinutesPerCall={
                                        numberOfMinutesPerCall
                                    }
                                />
                            </div>
                        </>
                    )}
                    {isProspection && (bookStep == 3 || bookStep == 4) && (
                        <>
                            <div className="flex flex-col gap-6 lg:flex-row lg:gap-0 lg:items-stretch">
                                {hostColumn(false)}
                                <div className="w-full lg:w-[350px] flex-shrink-0 lg:border-l lg:border-gray-200/60 dark:lg:border-borderDark lg:px-7">
                                    <Calendar
                                        selectedDate={onSelectDateFromCalendar}
                                        mentor={user}
                                        selectedTimezone={selectedTimezone}
                                        hideTimezoneSelector={true}
                                        mentee={
                                            session
                                                ? session.user
                                                : {
                                                      timezone:
                                                          selectedTimezone,
                                                  }
                                        }
                                        changeTimezone={onUpdateTimezone}
                                        setSlots={setSlots}
                                        numberOfMinutesPerCall={
                                            numberOfMinutesPerCall
                                        }
                                    />
                                </div>

                                {selectedDate &&
                                    selectedDateAvailableHours.length > 0 && (
                                        <motion.div
                                            ref={slotsRef}
                                            initial={{ opacity: 0, x: 16 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{
                                                duration: 0.3,
                                                ease: "easeOut",
                                            }}
                                            className="w-full lg:w-[220px] flex-shrink-0 lg:border-l lg:border-gray-200/60 dark:lg:border-borderDark lg:pl-7 scroll-mt-6"
                                        >
                                            <motion.div
                                                key={moment(selectedDate)
                                                    .tz(selectedTimezone)
                                                    .format("YYYY-MM-DD")}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ duration: 0.2 }}
                                                className="flex flex-col h-full"
                                            >
                                                <div className="flex items-center justify-between gap-2 mb-3">
                                                    <div className="text-[13px] font-semibold text-gray-900 dark:text-white capitalize truncate">
                                                        {capitalize(
                                                            moment(selectedDate)
                                                                .tz(
                                                                    selectedTimezone,
                                                                )
                                                                .locale(locale)
                                                                .format(
                                                                    "ddd D MMM",
                                                                ),
                                                        )}
                                                    </div>
                                                    <div className="inline-flex flex-shrink-0 rounded-md border border-gray-200 dark:border-borderDark p-0.5 text-[10px] font-semibold">
                                                        {["12h", "24h"].map(
                                                            (f) => (
                                                                <button
                                                                    key={f}
                                                                    type="button"
                                                                    onClick={() =>
                                                                        setTimeFormat(
                                                                            f,
                                                                        )
                                                                    }
                                                                    className={`px-1.5 py-0.5 rounded transition-colors ${
                                                                        timeFormat ===
                                                                        f
                                                                            ? "bg-gray-900 text-white dark:bg-white dark:text-grayDark-900"
                                                                            : "text-gray-400 hover:text-gray-700 dark:hover:text-grayDark-200"
                                                                    }`}
                                                                >
                                                                    {f}
                                                                </button>
                                                            ),
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex flex-col gap-2 overflow-y-auto pr-1 -mr-1 max-h-[19rem]">
                                                    {selectedDateAvailableHours.map(
                                                        (slot, i) => (
                                                            <motion.div
                                                                key={i}
                                                                initial={{
                                                                    opacity: 0,
                                                                    y: 6,
                                                                }}
                                                                animate={{
                                                                    opacity: 1,
                                                                    y: 0,
                                                                }}
                                                                transition={{
                                                                    duration: 0.18,
                                                                    delay: Math.min(
                                                                        i * 0.025,
                                                                        0.3,
                                                                    ),
                                                                }}
                                                            >
                                                                <Hour
                                                                    slot={slot}
                                                                    selectedTimezone={
                                                                        selectedTimezone
                                                                    }
                                                                    mentor={user}
                                                                    selectedDate={
                                                                        selectedDate
                                                                    }
                                                                    selectedSlot={
                                                                        selectedSlot
                                                                    }
                                                                    setSelectedSlot={
                                                                        setSelectedSlot
                                                                    }
                                                                    timeFormat={
                                                                        timeFormat
                                                                    }
                                                                />
                                                            </motion.div>
                                                        ),
                                                    )}
                                                </div>
                                                {selectedSlot && (
                                                    <motion.button
                                                        initial={{
                                                            opacity: 0,
                                                            y: 6,
                                                        }}
                                                        animate={{
                                                            opacity: 1,
                                                            y: 0,
                                                        }}
                                                        transition={{
                                                            duration: 0.2,
                                                        }}
                                                        type="button"
                                                        onClick={
                                                            onBookCallHandler
                                                        }
                                                        className="w-full mt-4 rounded-xl bg-primary btn-3d lip-orange text-white text-sm font-semibold py-2.5 hover:brightness-95 transition shadow-[0_6px_18px_-6px_rgba(232,81,29,0.55)]"
                                                    >
                                                        {t(
                                                            "BookNewCall_button_next",
                                                        )}
                                                    </motion.button>
                                                )}
                                            </motion.div>
                                        </motion.div>
                                    )}
                            </div>
                            <div ref={bookRef} className="h-0" />
                        </>
                    )}
                    {!isProspection && bookStep == 4 && (
                        <>
                            <div className="flex items-center gap-3 mb-4">
                                <button
                                    className="inline-flex items-center justify-center text-gray-400 cursor-pointer hover:text-gray-700 dark:text-grayDark-500 dark:hover:text-grayDark-200 transition-colors p-2 -ml-2"
                                    onClick={onBackHandler}
                                    aria-label="Retour"
                                    type="button"
                                >
                                    <SolarIcon
                                        name="alt-arrow-left-linear"
                                        className="text-2xl"
                                    />
                                </button>

                                <h2 className="text-xl font-bold dark:text-white">
                                    {t("BookNewCall_hour_title")}
                                </h2>
                            </div>
                            {creating ? (
                                <div className="flex justify-center items-center h-40">
                                    <SpinnerCircular
                                        size={50}
                                        thickness={150}
                                        color="#fff"
                                    />
                                </div>
                            ) : (
                                <>
                                    <div className="mt-3">
                                        {capitalize(
                                            moment(selectedDate)
                                                .tz(session.user.timezone)
                                                .locale(locale)
                                                .format("LL"),
                                        )}
                                        .
                                    </div>

                                    <div className="mt-3 flex flex-col gap-1">
                                        {selectedDateAvailableHours.map(
                                            (slot, i) => (
                                                <div key={i}>
                                                    <Hour
                                                        slot={slot}
                                                        selectedTimezone={
                                                            selectedTimezone
                                                        }
                                                        mentor={user}
                                                        selectedDate={
                                                            selectedDate
                                                        }
                                                        selectedSlot={
                                                            selectedSlot
                                                        }
                                                        setSelectedSlot={
                                                            setSelectedSlot
                                                        }
                                                    />
                                                </div>
                                            ),
                                        )}
                                    </div>
                                    <div className="mt-6">
                                        <Button
                                            type="primary"
                                            block
                                            onClick={onBookCallHandler}
                                            disabled={!selectedSlot}
                                        >
                                            {t("BookNewCall_button_next")}
                                        </Button>
                                    </div>
                                </>
                            )}
                            <div ref={bookRef} className="h-1 mt-4"></div>
                        </>
                    )}
                    {bookStep == "bookDetails" && (
                        <>
                            <div className="flex items-center gap-3 mb-4">
                                <button
                                    className="inline-flex items-center justify-center text-gray-400 cursor-pointer hover:text-gray-700 dark:text-grayDark-500 dark:hover:text-grayDark-200 transition-colors p-2 -ml-2"
                                    onClick={onBackHandler}
                                    aria-label="Retour"
                                    type="button"
                                >
                                    <SolarIcon
                                        name="alt-arrow-left-linear"
                                        className="text-2xl"
                                    />
                                </button>

                                <h2 className="text-xl font-bold dark:text-white">
                                    {t("BookNewCall_details_title")}
                                </h2>
                            </div>
                            <div className="mt-3">
                                {capitalize(
                                    selectedSlot.start
                                        .tz(selectedTimezone)
                                        .locale(locale)
                                        .format("LLL"),
                                )}
                                .
                            </div>
                            <div className="mt-5">
                                <label className="label">
                                    {t("GuestBooking_subject")}
                                </label>
                                <textarea
                                    rows={3}
                                    maxLength={450}
                                    className="input-textarea"
                                    placeholder={t(
                                        "GuestBooking_subject_placeholder",
                                        {
                                            name: `${user.firstName || ""} ${
                                                user.lastName || ""
                                            }`.trim(),
                                        },
                                    )}
                                    value={freeCallSubject}
                                    onChange={(e) =>
                                        setFreeCallSubject(e.target.value)
                                    }
                                />
                            </div>
                            <div className="mt-4">
                                <label className="label">
                                    {t("GuestBooking_preference")}
                                </label>
                                <div className="mt-1.5">
                                    <MeetingPreferenceSelector
                                        value={freeCallPreference}
                                        onChange={setFreeCallPreference}
                                        phoneNumber={freeCallPhone}
                                        onPhoneChange={setFreeCallPhone}
                                    />
                                </div>
                            </div>
                            <div className="mt-6">
                                <Button
                                    type="primary"
                                    block
                                    onClick={onConfirmCallDetails}
                                    disabled={
                                        !freeCallSubject.trim() ||
                                        !freeCallPreference ||
                                        (freeCallPreference === "phone" &&
                                            !isValidPhoneNumber(
                                                freeCallPhone || "",
                                            ))
                                    }
                                >
                                    {t("BookNewCall_button_next")}
                                </Button>
                            </div>
                        </>
                    )}
                    {bookStep == 5 && (
                        <>
                            <div className="flex items-center gap-3 mb-4">
                                <button
                                    className="inline-flex items-center justify-center text-gray-400 cursor-pointer hover:text-gray-700 dark:text-grayDark-500 dark:hover:text-grayDark-200 transition-colors p-2 -ml-2"
                                    onClick={onBackHandler}
                                    aria-label="Retour"
                                    type="button"
                                >
                                    <SolarIcon
                                        name="alt-arrow-left-linear"
                                        className="text-2xl"
                                    />
                                </button>

                                <h2 className="text-xl font-bold dark:text-white">
                                    {t("BookNewCall_payment_title")}
                                </h2>
                            </div>
                            <div className="mt-3">
                                {capitalize(
                                    selectedSlot.start
                                        .tz(selectedTimezone)
                                        .locale(locale)
                                        .format("LLL"),
                                )}
                                .
                            </div>
                            <div className="mt-3">
                                <div className="text-sm">
                                    {t("BookNewCall_payment_description")} (
                                    <Link href="/terms-of-sale" target="_blank">
                                        {t(
                                            "BookNewCall_payment_read",
                                        ).toLowerCase()}
                                    </Link>
                                    )
                                </div>
                                {clientSecret ? (
                                    <Elements
                                        options={{
                                            clientSecret,
                                            appearance: {
                                                theme: displayDarkStripe,
                                            },
                                        }}
                                        stripe={stripePromise}
                                    >
                                        <CheckoutFormMentor
                                            clientSecret={clientSecret}
                                            mentor={user}
                                            setBookIsPaid={setBookIsPaid}
                                            selectedSlot={selectedSlot}
                                            restartBooking={restartBooking}
                                            restartFullBooking={
                                                restartFullBooking
                                            }
                                            codeUsed={codeUsed}
                                        />
                                    </Elements>
                                ) : (
                                    <div className="flex justify-center mt-5">
                                        <SpinnerCircular
                                            color="#613BDB"
                                            thickness={190}
                                            size={30}
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                    {bookStep == "bookFreeCall" && (
                        isProspection ? (
                            <div className="flex flex-col gap-6 lg:flex-row lg:gap-0 lg:items-stretch">
                                {hostColumn(true)}
                                <div className="w-full lg:flex-1 lg:border-l lg:border-gray-200/60 dark:lg:border-borderDark lg:pl-7">
                                    <button
                                        type="button"
                                        onClick={onBackHandler}
                                        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-grayDark-400 dark:hover:text-white transition-colors mb-5"
                                    >
                                        <SolarIcon
                                            name="alt-arrow-left-linear"
                                            className="text-lg"
                                        />
                                        {t("BookNewCall_prospection_change_slot")}
                                    </button>
                                    <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
                                        {t("BookNewCall_prospection_confirm_title")}
                                    </h2>
                                    <div className="mt-5">
                                        <label className="label">
                                            {t("GuestBooking_subject")}
                                        </label>
                                        <textarea
                                            rows={3}
                                            className="input-textarea"
                                            placeholder={t(
                                                "GuestBooking_subject_placeholder",
                                                {
                                                    name: `${
                                                        user.firstName || ""
                                                    } ${
                                                        user.lastName || ""
                                                    }`.trim(),
                                                },
                                            )}
                                            value={freeCallSubject}
                                            onChange={(e) =>
                                                setFreeCallSubject(
                                                    e.target.value,
                                                )
                                            }
                                        />
                                    </div>
                                    <div className="mt-4">
                                        <label className="label">
                                            {t("GuestBooking_preference")}
                                        </label>
                                        <div className="mt-1.5">
                                            <MeetingPreferenceSelector
                                                value={freeCallPreference}
                                                onChange={setFreeCallPreference}
                                                phoneNumber={freeCallPhone}
                                                onPhoneChange={setFreeCallPhone}
                                            />
                                        </div>
                                    </div>
                                    <ProgressButton
                                        type="button"
                                        onClick={onBookFreeCallHandler}
                                        loading={updating}
                                        disabled={
                                            updating ||
                                            !freeCallSubject.trim() ||
                                            !freeCallPreference ||
                                            (freeCallPreference === "phone" &&
                                                !isValidPhoneNumber(
                                                    freeCallPhone || "",
                                                ))
                                        }
                                        overlayClassName="flex items-center justify-center text-sm font-semibold py-3"
                                        className="w-full mt-6 rounded-xl bg-primary btn-3d lip-orange text-white text-sm font-semibold py-3 hover:brightness-95 transition shadow-[0_6px_18px_-6px_rgba(232,81,29,0.55)] disabled:cursor-not-allowed flex items-center justify-center"
                                    >
                                        {t(
                                            "BookNewCall_prospection_confirm_button",
                                        )}
                                    </ProgressButton>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center gap-1">
                                    <div
                                        className="inline-block text-gray-400 cursor-pointer hover:text-gray-700 dark:text-grayDark-500 dark:hover:text-grayDark-200 transition-colors"
                                        onClick={onBackHandler}
                                    >
                                        <SolarIcon
                                            name="alt-arrow-left-linear"
                                            className="text-2xl"
                                        />
                                    </div>

                                    <h2 className="text-xl font-bold dark:text-white">
                                        {t("BookNewCall_payment_title")}
                                    </h2>
                                </div>
                                <div className="mt-3">
                                    {capitalize(
                                        selectedSlot.start
                                            .tz(selectedTimezone)
                                            .locale(locale)
                                            .format("LLL"),
                                    )}
                                    .
                                </div>
                                <div className="mt-3">
                                    <div className="text-sm">
                                        {t(
                                            "BookNewCall_payment_free_description",
                                        )}
                                    </div>
                                    <div className="mt-5">
                                        <Button
                                            className="w-full"
                                            type="primary"
                                            onClick={onBookFreeCallHandler}
                                            isLoading={updating}
                                            progress
                                        >
                                            {t(
                                                "BookNewCall_button_bookAFreeCall",
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )
                    )}
                    {bookStep == "bookDiscovery" && (
                        <>
                            <div className="flex items-center gap-1">
                                <div
                                    className="inline-block text-gray-400 cursor-pointer hover:text-gray-700 dark:text-grayDark-500 dark:hover:text-grayDark-200 transition-colors"
                                    onClick={onBackHandler}
                                >
                                    <SolarIcon
                                        name="alt-arrow-left-linear"
                                        className="text-2xl"
                                    />
                                </div>
                                <h2 className="text-xl font-bold dark:text-white">
                                    {t("BookNewCall_discovery_confirm_title")}
                                </h2>
                            </div>
                            <div className="mt-3">
                                {capitalize(
                                    selectedSlot.start
                                        .tz(selectedTimezone)
                                        .locale(locale)
                                        .format("LLL"),
                                )}
                                .
                            </div>
                            <div className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-confidence">
                                <SolarIcon
                                    name="gift-linear"
                                    className="text-base"
                                />
                                {t("BookNewCall_discovery_free_badge", {
                                    minutes:
                                        numberOfMinutesPerCall ||
                                        user.minutesPerCall,
                                })}
                            </div>
                            <div className="mt-4">
                                <label className="label">
                                    {t("GuestBooking_subject")}
                                </label>
                                <textarea
                                    rows={3}
                                    className="input-textarea"
                                    placeholder={t(
                                        "GuestBooking_subject_placeholder",
                                        {
                                            name: `${user.firstName || ""} ${
                                                user.lastName || ""
                                            }`.trim(),
                                        },
                                    )}
                                    value={freeCallSubject}
                                    onChange={(e) =>
                                        setFreeCallSubject(e.target.value)
                                    }
                                />
                            </div>
                            <div className="mt-4">
                                <label className="label">
                                    {t("GuestBooking_preference")}
                                </label>
                                <div className="mt-1.5">
                                    <MeetingPreferenceSelector
                                        value={freeCallPreference}
                                        onChange={setFreeCallPreference}
                                        phoneNumber={freeCallPhone}
                                        onPhoneChange={setFreeCallPhone}
                                    />
                                </div>
                            </div>
                            <ProgressButton
                                type="button"
                                onClick={onBookDiscoveryHandler}
                                loading={updating}
                                disabled={
                                    updating ||
                                    !freeCallPreference ||
                                    (freeCallPreference === "phone" &&
                                        !isValidPhoneNumber(freeCallPhone || ""))
                                }
                                overlayClassName="flex items-center justify-center text-sm font-semibold py-3"
                                className="w-full mt-6 rounded-xl bg-primary btn-3d lip-orange text-white text-sm font-semibold py-3 hover:brightness-95 transition shadow-[0_6px_18px_-6px_rgba(232,81,29,0.55)] disabled:cursor-not-allowed flex items-center justify-center"
                            >
                                {t("BookNewCall_discovery_confirm_button")}
                            </ProgressButton>
                        </>
                    )}
                    {bookStep == "guestForm" && (
                        <div className="flex flex-col gap-6 lg:flex-row lg:gap-0 lg:items-stretch">
                            {hostColumn(true)}
                            <div className="w-full lg:flex-1 lg:border-l lg:border-gray-200/60 dark:lg:border-borderDark lg:pl-7">
                                <GuestBookingForm
                                    mentor={user}
                                    selectedSlot={selectedSlot}
                                    selectedTimezone={selectedTimezone}
                                    prospectionToken={prospectionToken}
                                    onBack={onBackHandler}
                                    onBooked={() => setBookIsPaid(true)}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
